import BaseObject from '../../base/base_object';
import * as GNWP_CONSTS from './gnwp_consts';
import _ from '../../utils/underscore';

export default class GNWPResponse extends BaseObject {
    private navigationParams: any;
    private baseUrl: string;

    init(params) {
        this
            ._setBaseUrl(params.baseUrl)
            ._setNavigationParams(params.navigation);
    }


    /**
     * Set GNWP navigation parameters that has been received from GNWP API request
     * @param params
     * @returns {GNWPResponse}
     * @private
     */
    private _setNavigationParams(navigation) {
        this.navigationParams = navigation;
        this.logger.debug('GNWP navigation params', this.navigationParams);
        return this;
    }


    /**
     * Set base url to GNWP navigation
     * @param url
     * @returns {GNWPResponse}
     * @private
     */
    private _setBaseUrl(url) {
        this.baseUrl = url;
        return this;
    }

    /**
     * Return GNWP full url
     *
     * @usage
     * Open web resource, for example, game in new window
     * 
     * @example
     * let GNWPResponseObject = getObject();
     * let url = GNWPResponseObject.getGNWPUrl();
     * window.open(url);
     * 
     * @returns {string}
     */
    getGNWPUrl(): string {
        const url = this.baseUrl + _.jsonToQueryString(this.navigationParams);
        return url;
    }


    /**
     * Return GNWP predefined (filled) form to perform GNWP navigation through POST
     * 
     * @usage
     * Open web resource, for example, game in iframe
     * 
     * @example
     * let GNWPResponseObject = getObject();
     * let form = GNWPResponseObject.getGNWPPostForm();
     * form.setAttribute('target', 'your iframe name');
     * document.body.appendChild(form);
     * form.submit();
     * 
     * @returns {Element}
     */
    getGNWPPostForm(): HTMLFormElement {
        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', this.baseUrl);

        for(const prop in this.navigationParams) {
            const hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', prop);
            hiddenField.setAttribute('value', this.navigationParams[prop]);

            form.appendChild(hiddenField);
        }

        return form;
    }


    /**
     * Performs GNWP navigation to specified target (window or iframe) through HTTP method GET or POST
     * Encapsulates getGNWPUrl and getGNWPPostForm methods
     * Note: target 'window' can be opened only via GET request
     * 
     * @param {string} method - HTTP method that will be performed to navigation. 'GET' or 'POST'
     * @param {string} target - specified target where GNWP navigation response will be injected ('WINDOW' or 'IFRAME')
     * @param {string} frameName - required when navigation result should be injected to iframe (target = 'IFRAME') 
     */
    performGNWPNavigation(method, target, frameName) {
        switch(target) {
            case GNWP_CONSTS.TARGET_VIEWS.IFRAME:
                this._performToIframeNavigation(method, frameName);
                break;
            case GNWP_CONSTS.TARGET_VIEWS.WINDOW:
                this._performToNewWindowNavigation(method);
                break;
            default:
                break;
        }
    }

    /**
     * Perforn GNWP navigation and through iframe.
     * 
     * @param method
     * @param frameName
     * @private
     */
    private _performToIframeNavigation(method, frameName) {

        if (!_.isString(method) || !_.isString(frameName)) {
           throw new Error('GNWP navigation failed. method or frameName is not provided');
        }

        switch (method) {
            case GNWP_CONSTS.REQUEST_METHODS.GET:
                const url = this.getGNWPUrl();
                const frame = document.getElementsByName(frameName) as any;
                frame.src = url;
                break;
            case GNWP_CONSTS.REQUEST_METHODS.POST:
                const form = this.getGNWPPostForm();
                form.setAttribute('target', frameName);
                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
                break;
        }
    }


    /**
     * Perform GNWP navigation in new window
     * @param method
     * @private
     */
    private _performToNewWindowNavigation(method) {
        if (!_.isString(method)) {
            throw new Error('GNWP navigation failed. method is not provided');
        }

        if (method !== GNWP_CONSTS.REQUEST_METHODS.GET) {
            throw new Error('GNWP navigation failed. Impossible open new window with method POST');
        }

        const url = this.getGNWPUrl();
        window.open(url);
    }
}
